import React, { useState, useEffect } from 'react';
import '../styles/SideNavigation.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Home01Icon, Settings01Icon, UserCircleIcon, Coins01Icon, Plug02Icon, ShoppingBag01Icon, MoneyReceiveCircleIcon } from '@hugeicons/core-free-icons';
import { colour, numberFormatter } from '../styles/GlobalStyles';
import { useCompany, useUI } from '../contexts';
import { recordEvent, getBuyers, cloudFunctionV2 } from '../functions';
import { ChevronWide } from '../assets/icons';
import DefaultUserImage from './DefaultUserImage';
import VerticalTab from './VerticalTab';
import IconButton from './IconButton';

function SideNavigation() {
    const navigate = useNavigate();
    const location = useLocation();
    const companyContext = useCompany();
    const { sidebarCollapsed, setSidebarCollapsed } = useUI();

    const companyName = companyContext.companyName || "";
    const companyLogo = companyContext.companyLogo || null;
    const credits = companyContext.credits ?? null;
    const companyId = companyContext.companyId || null;

    const [hasBuyers, setHasBuyers] = useState(false);
    const [hasDeals, setHasDeals] = useState(false);

    const toggleSidebar = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    useEffect(() => {
        const checkBuyers = async () => {
            if (!companyId) return;
            try {
                const result = await getBuyers(companyId);
                setHasBuyers(result.status === 200 && result.data?.length > 0);
            } catch (error) {
                console.error('Error checking buyers:', error);
            }
        };
        checkBuyers();
    }, [companyId]);

    useEffect(() => {
        const checkDeals = async () => {
            if (!companyId) return;
            try {
                const [offersResult, sentLeadsResult] = await Promise.all([
                    cloudFunctionV2(process.env.REACT_APP_GET_ORDERED_DOCS, {
                        collection: 'companies',
                        subcollection: 'offers',
                        document: companyId,
                        field: 'data.createdAt',
                        order: 'desc',
                        limit: 1,
                        page: 1,
                    }),
                    cloudFunctionV2(process.env.REACT_APP_GET_ORDERED_DOCS, {
                        collection: 'companies',
                        subcollection: 'leads',
                        document: companyId,
                        field: 'data.updatedAt',
                        order: 'desc',
                        limit: 1,
                        page: 1,
                        whereField: 'data.leadReviewStatus',
                        whereValue: 'sent',
                    }),
                ]);
                const hasOffers = offersResult.status === 200 && offersResult.data?.length > 0;
                const hasSentDeals = sentLeadsResult.status === 200 && sentLeadsResult.data?.length > 0;
                setHasDeals(hasOffers || hasSentDeals);
            } catch (error) {
                console.error('Error checking deals:', error);
            }
        };
        checkDeals();
    }, [companyId]);

    const navigationItems = [
        {
            label: "Home",
            icon: Home01Icon,
            path: "/",
            active: location.pathname === "/"
        },
        ...(hasBuyers ? [{
            label: "Buyers",
            icon: ShoppingBag01Icon,
            path: "/buyers",
            active: location.pathname === "/buyers"
        }] : []),
        ...(hasDeals ? [{
            label: "Deals",
            icon: MoneyReceiveCircleIcon,
            path: "/deals",
            active: location.pathname === "/deals" || location.pathname.startsWith("/deals/")
        }] : [])
    ];

    const bottomItems = [
        {
            label: "Integrations",
            icon: Plug02Icon,
            path: "/integrations",
            active: location.pathname === "/integrations"
        },
        {
            label: "Settings",
            icon: Settings01Icon,
            path: "/settings",
            active: location.pathname === "/settings"
        },
        {
            label: "Profile",
            icon: UserCircleIcon,
            path: "/profile",
            active: location.pathname === "/profile"
        }
    ];

    const handleNavigation = (path) => {
        recordEvent('side_nav_clicked', { destination: path });
        navigate(path);
    };

    return (
        <div className={`side-navigation-container ${sidebarCollapsed ? 'side-navigation-collapsed' : ''}`}>
            <div className="side-navigation-top">
                <div className={`side-navigation-toggle ${sidebarCollapsed ? 'side-navigation-toggle-collapsed' : ''}`}>
                    <IconButton
                        size="small"
                        icon={
                            <ChevronWide
                                fill={colour.gray05}
                                className={sidebarCollapsed ? 'side-navigation-chevron-collapsed' : 'side-navigation-chevron-expanded'}
                            />
                        }
                        disabled={false}
                        clickFunc={toggleSidebar}
                        standardIcon={false}
                        background={false}
                        contextTooltip={false}
                    />
                </div>
                {sidebarCollapsed ? (
                    navigationItems.map((item, index) => (
                        <div
                            key={index}
                            className={`side-navigation-icon-only ${item.active ? 'side-navigation-icon-active' : ''}`}
                            onClick={() => handleNavigation(item.path)}
                        >
                            <HugeiconsIcon icon={item.icon} color={item.active ? colour.gray06 : colour.gray05} size={16} />
                        </div>
                    ))
                ) : (
                    navigationItems.map((item, index) => (
                        <VerticalTab
                            key={index}
                            selected={item.active}
                            label={item.label}
                            disabled={false}
                            cta={() => handleNavigation(item.path)}
                            icon={item.icon ? <HugeiconsIcon icon={item.icon} color={item.active ? colour.gray06 : colour.gray05} size={16} /> : null}
                            standardIcon={false}
                            external={false}
                        />
                    ))
                )}
            </div>
            <div className="side-navigation-bottom">
                {sidebarCollapsed ? (
                    bottomItems.map((item, index) => (
                        <div
                            key={index}
                            className={`side-navigation-icon-only ${item.active ? 'side-navigation-icon-active' : ''}`}
                            onClick={() => handleNavigation(item.path)}
                        >
                            <HugeiconsIcon icon={item.icon} color={item.active ? colour.gray06 : colour.gray05} size={16} />
                        </div>
                    ))
                ) : (
                    bottomItems.map((item, index) => (
                        <VerticalTab
                            key={index}
                            selected={item.active}
                            label={item.label}
                            disabled={false}
                            cta={() => handleNavigation(item.path)}
                            icon={item.icon ? <HugeiconsIcon icon={item.icon} color={item.active ? colour.gray06 : colour.gray05} size={16} /> : null}
                            standardIcon={false}
                            external={false}
                        />
                    ))
                )}
                <div className="side-navigation-divider"></div>
                <div className={`side-navigation-company ${sidebarCollapsed ? 'side-navigation-company-collapsed' : ''}`}>
                    <div>
                        <DefaultUserImage
                            size="large"
                            clickFunc={null}
                            image={companyLogo}
                        />
                    </div>
                    {!sidebarCollapsed && (
                        <div className="side-navigation-company-info">
                            <span className="body-semibold colour-primary">
                                {companyName}
                            </span>
                            <div className="side-navigation-company-credits">
                                <HugeiconsIcon
                                    icon={Coins01Icon}
                                    color={colour.gray05}
                                    size={16}
                                />
                                <span className="body-regular colour-secondary">
                                    {numberFormatter.format(credits ?? 0)} credits
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SideNavigation;

